import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, Building2, Tag, Layers } from "lucide-react";
import { Reveal, CTASection, usePageMeta, Eyebrow } from "../components/ui-bits";
import { IMG, imgUrl, projects, projectDetails } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const staticProject = (id) => {
  const p = projects.find((x) => x.id === id);
  if (!p) return null;
  const det = projectDetails[id] || {};
  return { ...p, scopeList: det.scopeList || [], equipment: det.equipment || [], gallery: [], seo_title: "", seo_description: "" };
};

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(() => staticProject(id));
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const fallback = staticProject(id);
    setProject(fallback);
    setMissing(false);
    axios.get(`${API}/projects/${id}`)
      .then((r) => setProject(r.data))
      .catch((e) => {
        if (e.response?.status === 404 && !fallback) setMissing(true);
      });
  }, [id]);

  usePageMeta(
    project ? (project.seo_title || `${project.title} — ${project.client} | G. R. Engineering Projects`) : "Project | G. R. Engineering",
    project ? (project.seo_description || `${project.title} — ${project.client}, ${project.location}. ${project.scope}`) : ""
  );
  if (missing) return <Navigate to="/projects" replace />;
  if (!project) return <div className="container-gre py-32 text-center text-slate-400">Loading…</div>;
  const details = project;
  const idx = Math.max(projects.findIndex((p) => p.id === id), 0);
  const prev = projects[(idx - 1 + projects.length) % projects.length];
  const next = projects[(idx + 1) % projects.length];

  return (
    <>
      <section className="relative bg-[#0A1128] overflow-hidden" data-testid="project-detail-hero">
        <div className="absolute inset-0">
          <img src={imgUrl(project.img)} alt="" className="w-full h-full object-cover img-industrial opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/85 to-[#0A1128]/40" />
        </div>
        <div className="container-gre relative py-20 lg:py-28">
          <Reveal>
            <Link to="/projects" data-testid="back-to-projects-link" className="inline-flex items-center gap-2 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-400 hover:text-white transition-colors duration-200">
              <ArrowLeft size={14} /> All Projects
            </Link>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-8 font-mono-tech text-xs font-semibold uppercase tracking-[0.25em] text-red-400">{project.client}</p>
          </Reveal>
          <Reveal delay={0.14}>
            <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.08] max-w-4xl">{project.title}</h1>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-slate-300">
              <span className="flex items-center gap-2"><MapPin size={15} className="text-[#E8574F]" />{project.location}</span>
              <span className={`font-mono-tech text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 ${project.status.includes("Ongoing") ? "bg-[#C5221F] text-white" : "bg-white/10 text-slate-200 border border-white/20"}`}>{project.status}</span>
              {project.categories.map((c) => (
                <span key={c} className="font-mono-tech text-[10px] font-semibold uppercase tracking-widest text-slate-400 border border-white/15 px-3 py-1.5">{c}</span>
              ))}
            </div>
          </Reveal>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#C5221F]" />
      </section>

      <section className="bg-white" data-testid="project-detail-body">
        <div className="container-gre py-16 lg:py-24 grid lg:grid-cols-3 gap-12 lg:gap-16">
          <div className="lg:col-span-2">
            <Reveal><Eyebrow>Scope of Work</Eyebrow></Reveal>
            <Reveal delay={0.06}>
              <p className="mt-5 text-base lg:text-lg leading-relaxed text-slate-700">{project.scope}</p>
            </Reveal>
            {details?.scopeList && (
              <Reveal delay={0.12}>
                <ul className="mt-8 space-y-3.5">
                  {details.scopeList.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-slate-700 border-l-2 border-[#C5221F] pl-4 py-0.5">{s}</li>
                  ))}
                </ul>
              </Reveal>
            )}
            {details?.equipment && (
              <div className="mt-12">
                <Reveal><Eyebrow>Equipment & Package Details</Eyebrow></Reveal>
                <Reveal delay={0.08}>
                  <div className="mt-6 border border-slate-200 overflow-x-auto" data-testid="project-equipment-table">
                    <table className="w-full text-left min-w-[560px]">
                      <thead>
                        <tr className="bg-[#0A1128]">
                          <th className="px-5 py-3.5 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-white">Equipment</th>
                          <th className="px-5 py-3.5 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-white">Tag / Unit</th>
                          <th className="px-5 py-3.5 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-white">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.equipment.map(([item, tag, spec], i) => (
                          <tr key={item} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                            <td className="px-5 py-3.5 text-sm font-semibold text-slate-900 border-t border-slate-200">{item}</td>
                            <td className="px-5 py-3.5 text-[13px] text-slate-600 border-t border-slate-200">{tag}</td>
                            <td className="px-5 py-3.5 text-[13px] font-medium text-slate-800 border-t border-slate-200">{spec}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Reveal>
              </div>
            )}
            {!!project.gallery?.length && (
              <div className="mt-12">
                <Reveal><Eyebrow>Project Gallery</Eyebrow></Reveal>
                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  {project.gallery.map((g, i) => (
                    <Reveal key={i} delay={i * 0.06}>
                      <img src={g} alt={`${project.title} — photo ${i + 1}`} loading="lazy" className="w-full aspect-video object-cover img-industrial border border-slate-200" />
                    </Reveal>
                  ))}
                </div>
              </div>
            )}
          </div>
          <aside className="space-y-6">
            <Reveal>
              <img src={imgUrl(project.img)} alt={project.title} className="w-full aspect-[4/3] object-cover img-industrial border border-slate-200" />
            </Reveal>
            <Reveal delay={0.08}>
              <div className="border border-slate-200" data-testid="project-facts-card">
                <p className="px-5 py-3.5 bg-slate-50 border-b border-slate-200 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-500">Project Facts</p>
                <dl>
                  {[
                    [Building2, "Client", project.client],
                    [MapPin, "Location", project.location],
                    [Layers, "Categories", project.categories.join(" · ")],
                    [Tag, "Status", project.status],
                  ].map(([Icon, k, v], i) => (
                    <div key={k} className={`px-5 py-3.5 ${i > 0 ? "border-t border-slate-200" : ""}`}>
                      <dt className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400"><Icon size={12} className="text-[#C5221F]" />{k}</dt>
                      <dd className="mt-1 text-sm font-semibold text-slate-900">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="bg-[#0A1128] p-7">
                <p className="font-display text-lg font-bold text-white">Planning something similar?</p>
                <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">Talk to the team that engineered and executed this scope.</p>
                <Link to={`/contact?interest=${encodeURIComponent(project.categories.includes("EPC / LSTK") ? "EPC / LSTK Project" : project.categories.includes("Site Fabrication") ? "Site Fabrication" : "General Enquiry")}`} data-testid="project-detail-enquire-btn" className="group mt-5 inline-flex items-center gap-2.5 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-6 py-3.5 text-xs uppercase tracking-widest transition-colors duration-300">
                  Discuss Your Project <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200" data-testid="project-detail-nav">
        <div className="container-gre grid sm:grid-cols-2 gap-px bg-slate-200">
          <Link to={`/projects/${prev.id}`} data-testid="prev-project-link" className="group bg-slate-50 hover:bg-white p-7 transition-colors duration-200">
            <p className="flex items-center gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400"><ArrowLeft size={13} /> Previous Project</p>
            <p className="mt-2.5 font-display text-sm font-bold text-slate-900 group-hover:text-[#C5221F] transition-colors duration-200 line-clamp-2">{prev.title}</p>
          </Link>
          <Link to={`/projects/${next.id}`} data-testid="next-project-link" className="group bg-slate-50 hover:bg-white p-7 text-right transition-colors duration-200">
            <p className="flex items-center justify-end gap-2 font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400">Next Project <ArrowRight size={13} /></p>
            <p className="mt-2.5 font-display text-sm font-bold text-slate-900 group-hover:text-[#C5221F] transition-colors duration-200 line-clamp-2">{next.title}</p>
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default ProjectDetail;
